import type { PromptContent } from "@seedance/contracts";
import { promptLength, promptOffsetToPoint, promptPointToOffset, type PromptPoint, type PromptRange } from "./prompt-edit-operations.js";

export function segmentDomPoint(segmentIndex: number, kind: "text" | "reference", offset: number, textLength: number): PromptPoint {
  return { segmentIndex, offset: kind === "reference" ? offset > 0 ? 1 : 0 : Math.max(0, Math.min(offset, textLength)) };
}

/** Only explicit React segment wrappers are addressable; chip descendants are atomic. */
export function domPointToPromptPoint(root: HTMLElement, content: PromptContent, node: Node, offset: number): PromptPoint | null {
  if (node !== root && !root.contains(node)) return null;
  if (!content.segments.length) return { segmentIndex: 0, offset: 0 };
  if (node === root) {
    const next = Array.from(root.childNodes).slice(offset).find(child => child instanceof HTMLElement && child.hasAttribute("data-prompt-segment")) as HTMLElement | undefined;
    return next ? { segmentIndex: Number(next.dataset.promptSegment), offset: 0 } : promptOffsetToPoint(content, promptLength(content));
  }
  const element = node.nodeType === 1 ? node as Element : node.parentElement;
  const wrapper = element?.closest<HTMLElement>("[data-prompt-segment]");
  if (!wrapper || wrapper.parentElement !== root) return null;
  const index = Number(wrapper.dataset.promptSegment), segment = content.segments[index];
  if (!segment) return null;
  if (segment.type === "reference") return segmentDomPoint(index, "reference", offset, 0);
  const range = root.ownerDocument.createRange();
  range.setStart(wrapper, 0);
  range.setEnd(node, offset);
  return segmentDomPoint(index, "text", range.toString().length, segment.text.length);
}

export function readPromptSelection(root: HTMLElement, content: PromptContent): PromptRange | null {
  const selection = root.ownerDocument.getSelection();
  if (!selection?.rangeCount) return null;
  const range = selection.getRangeAt(0);
  const start = domPointToPromptPoint(root, content, range.startContainer, range.startOffset);
  const end = domPointToPromptPoint(root, content, range.endContainer, range.endOffset);
  return start && end ? { start, end } : null;
}

export function promptPointToDomPoint(root: HTMLElement, content: PromptContent, point: PromptPoint): { node: Node; offset: number } {
  promptPointToOffset(content, point);
  const wrapper = root.querySelector<HTMLElement>(`[data-prompt-segment="${point.segmentIndex}"]`);
  if (!wrapper) return { node: root, offset: 0 };
  if (content.segments[point.segmentIndex]?.type === "reference") {
    return { node: root, offset: Array.from(root.childNodes).indexOf(wrapper) + point.offset };
  }
  // React emits one text node; walking also handles native composition splitting it.
  const walker = root.ownerDocument.createTreeWalker(wrapper, 4);
  let offset = point.offset, node = walker.nextNode();
  while (node) {
    const length = node.textContent?.length ?? 0;
    if (offset <= length) return { node, offset };
    offset -= length; node = walker.nextNode();
  }
  return { node: wrapper, offset: wrapper.childNodes.length };
}

export function restorePromptSelection(root: HTMLElement, content: PromptContent, range: PromptRange): void {
  const start = promptPointToDomPoint(root, content, range.start), end = promptPointToDomPoint(root, content, range.end);
  const domRange = root.ownerDocument.createRange();
  domRange.setStart(start.node, start.offset); domRange.setEnd(end.node, end.offset);
  const selection = root.ownerDocument.getSelection();
  selection?.removeAllRanges(); selection?.addRange(domRange);
}

export function promptRangeAtClientPoint(root: HTMLElement, content: PromptContent, x: number, y: number): PromptRange | null {
  const document = root.ownerDocument as Document & { caretRangeFromPoint?(x: number, y: number): Range | null };
  const range = document.caretRangeFromPoint?.(x, y);
  if (!range) return null;
  const point = domPointToPromptPoint(root, content, range.startContainer, range.startOffset);
  return point ? { start: point, end: point } : null;
}
